import { formatDistanceToNowStrict } from 'date-fns';
import { useCallback } from 'react';

import { useTrackClickedDeleteInviteLink, useTrackCopiedInviteMemberLink } from '@air/analytics';
import { type WorkspaceUserRole } from '@air/api/types';
import { parseAirDateToISO } from '@air/utilities';

import { CopyField } from '~/components/UI/CopyField';
import { Routes } from '~/constants/routes';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useWorkspaceRolesContext } from '~/providers/WorkspaceRolesProvider';
import { useDeleteInviteToken } from '~/swr-hooks/members/useDeleteInviteToken';
import { useInviteTokens } from '~/swr-hooks/members/useInviteTokens';
import { canDeleteWorkspaceInviteLink } from '~/utils/permissions/workspacePermissions';

type InviteLinksListProps = {
  roles: WorkspaceUserRole[];
};

type InviteLinkCopyFieldProps = {
  menuActions: { text: string; action: () => void }[];
  roleId: string;
  url: string;
};

const InviteLinkCopyField = ({ menuActions, roleId, url }: InviteLinkCopyFieldProps) => {
  const { data: allRoles } = useWorkspaceRolesContext();
  const { trackCopiedInviteMemberLink } = useTrackCopiedInviteMemberLink();

  const onCopy = useCallback(() => {
    const role = allRoles?.find((role) => role.id === roleId);
    trackCopiedInviteMemberLink({ roleId, role: role?.displayName });
  }, [allRoles, roleId, trackCopiedInviteMemberLink]);

  return <CopyField url={url} menuActions={menuActions} onCopy={onCopy} />;
};

export const InviteLinksList = ({ roles }: InviteLinksListProps) => {
  const { data = [] } = useInviteTokens();
  const { deleteInviteToken } = useDeleteInviteToken();
  const { trackClickedDeleteInviteLink } = useTrackClickedDeleteInviteLink();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  if (!roles.length || !data) {
    return null;
  }

  return (
    <div className="mt-6">
      {Boolean(data.length) && (
        <div className="mb-2.5 font-semibold ads:text-secondary text-14 text-pigeon-500">Active Invite Links</div>
      )}

      {data?.map((item, index) => {
        const createdAt = parseAirDateToISO(item.createdAt);

        const url = `${window.location.origin}${Routes.joinWorkspaceWithToken.replace('[shortId]', item.value)}`;

        const menuActions = [];

        if (canDeleteWorkspaceInviteLink(workspacePermissions)) {
          menuActions.push({
            text: 'Delete Link',
            action: () => {
              void deleteInviteToken(item.id);
              trackClickedDeleteInviteLink();
            },
          });
        }

        return (
          <div className="mb-4" key={index}>
            <div className="mt-1.5 flex shrink-0 items-center">
              <InviteLinkCopyField menuActions={menuActions} roleId={item.roleId} url={url} />
            </div>
            <div className="mt-1.5 flex items-center">
              <div className="ads:text-metadata text-12 text-pigeon-400">
                {roles.find((role) => role.id === item.roleId)?.displayName} permissions
              </div>
              <span className="mx-1.5 size-[3px] rounded-full ads:bg-ads-gray-8 bg-pigeon-400" />
              <div className="ads:text-metadata text-12 text-pigeon-400">
                created {formatDistanceToNowStrict(createdAt, { addSuffix: true })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
